import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import type { Assessment } from '../../../store/impactStore';

// ── ICS 209 — Incident Status Summary ──
export function generateICS209(incident: Record<string, unknown>, assessments: Assessment[], orgName: string) {
  const doc = new jsPDF();
  const now = format(new Date(), 'MM/dd/yyyy HH:mm');

  doc.setFontSize(14);
  doc.setFont(undefined as unknown as string, 'bold');
  doc.text('ICS 209 — INCIDENT STATUS SUMMARY', 105, 15, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont(undefined as unknown as string, 'normal');

  const fields = [
    ['1. Incident Name', String(incident.name || '')],
    ['2. Incident Number', String(incident.id || '').slice(0, 8)],
    ['3. Report Date/Time', now],
    ['4. Incident Type', String(incident.incidentType || '').toUpperCase()],
    ['5. Incident Location', String(incident.location || '')],
    ['6. County/Region', ''],
    ['7. State', ''],
    ['8. Start Date', incident.startDate ? String(incident.startDate) : ''],
    ['9. Prepared By', orgName],
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = doc as any;

  autoTable(doc, {
    startY: 22,
    body: fields,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 }, 1: { cellWidth: 130 } },
  });

  const stats = computeAssessmentStats(assessments);
  doc.setFontSize(11);
  doc.setFont(undefined as unknown as string, 'bold');
  doc.text('DAMAGE SUMMARY', 14, d.lastAutoTable.finalY + 12);

  autoTable(doc, {
    startY: d.lastAutoTable.finalY + 16,
    head: [['Category', 'Count', '% of Total']],
    body: [
      ['Affected', stats.affected, pct(stats.affected, stats.total)],
      ['Minor Damage', stats.minor, pct(stats.minor, stats.total)],
      ['Major Damage', stats.major, pct(stats.major, stats.total)],
      ['Destroyed', stats.destroyed, pct(stats.destroyed, stats.total)],
      ['Inaccessible', stats.inaccessible, pct(stats.inaccessible, stats.total)],
      ['TOTAL', stats.total, '100%'],
    ],
    theme: 'grid',
    styles: { fontSize: 9 },
    headStyles: { fillColor: [17, 45, 78] },
  });

  autoTable(doc, {
    startY: d.lastAutoTable.finalY + 8,
    head: [['Severity', 'Count']],
    body: [1, 2, 3, 4, 5].map(s => [`Severity ${s}`, stats.bySeverity[s] || 0]),
    theme: 'grid',
    styles: { fontSize: 9 },
    headStyles: { fillColor: [17, 45, 78] },
  });

  doc.setFontSize(7);
  doc.text(`Generated by OpenGov Impact — ${now}`, 105, 285, { align: 'center' });

  return doc;
}

// ── PDA Summary Report ──
export function generatePDASummary(incident: Record<string, unknown>, assessments: Assessment[], _orgName: string) {
  const doc = new jsPDF('landscape');
  const now = format(new Date(), 'MM/dd/yyyy HH:mm');

  doc.setFontSize(14);
  doc.setFont(undefined as unknown as string, 'bold');
  doc.text('PRELIMINARY DAMAGE ASSESSMENT — SUMMARY REPORT', 148, 15, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont(undefined as unknown as string, 'normal');
  doc.text(`Incident: ${incident.name || ''} | Date: ${now}`, 148, 22, { align: 'center' });

  const iaAssessments = assessments.filter(a => a.assessmentType === 'individual');

  autoTable(doc, {
    startY: 28,
    head: [['#', 'Address', 'Category', 'Severity', 'Residence', 'Owner/Renter', 'Habitable', 'Insured', 'Water Line', 'Assessed']],
    body: iaAssessments.map((a, i) => [
      i + 1,
      a.address || '',
      (a.damageCategory || '').toUpperCase(),
      a.severity || '',
      a.residenceType || '',
      a.ownership || '',
      a.habitable != null ? (a.habitable ? 'Yes' : 'No') : '',
      a.insured != null ? (a.insured ? 'Yes' : 'No') : '',
      a.waterLineHeight ? `${a.waterLineHeight}"` : 'N/A',
      a.datecreated || '',
    ]),
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [17, 45, 78], fontSize: 7 },
  });

  doc.setFontSize(7);
  doc.text(`Generated by OpenGov Impact — ${now}`, 148, doc.internal.pageSize.height - 8, { align: 'center' });

  return doc;
}

// ── PA (Public Assistance) Summary ──
export function generatePASummary(incident: Record<string, unknown>, assessments: Assessment[], _orgName: string) {
  const doc = new jsPDF('landscape');
  const now = format(new Date(), 'MM/dd/yyyy HH:mm');

  doc.setFontSize(14);
  doc.setFont(undefined as unknown as string, 'bold');
  doc.text('PUBLIC ASSISTANCE — DAMAGE SUMMARY', 148, 15, { align: 'center' });

  doc.setFontSize(9);
  doc.text(`Incident: ${incident.name || ''} | Date: ${now}`, 148, 22, { align: 'center' });

  const paCategories: Record<string, string> = {
    A: 'Debris Removal',
    B: 'Emergency Protective Measures',
    C: 'Roads & Bridges',
    D: 'Water Control Facilities',
    E: 'Public Buildings & Contents',
    F: 'Public Utilities',
    G: 'Parks, Recreational & Other',
  };

  const paAssessments = assessments.filter(a => a.assessmentType === 'public' || a.assessmentType === 'infrastructure');

  autoTable(doc, {
    startY: 28,
    head: [['#', 'Facility', 'PA Category', 'Severity', 'Location', 'Description', 'Assessed']],
    body: paAssessments.map((a, i) => [
      i + 1,
      a.facilityName || '',
      a.paCategory ? `Cat ${a.paCategory} — ${paCategories[a.paCategory] || ''}` : '',
      a.severity || '',
      a.address || '',
      (a.description || '').slice(0, 60),
      a.datecreated || '',
    ]),
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [17, 45, 78], fontSize: 7 },
  });

  doc.setFontSize(7);
  doc.text(`Generated by OpenGov Impact — ${now}`, 148, doc.internal.pageSize.height - 8, { align: 'center' });

  return doc;
}

// ── Helpers ──
interface AssessmentStats {
  total: number;
  affected: number;
  minor: number;
  major: number;
  destroyed: number;
  inaccessible: number;
  bySeverity: Record<number, number>;
  [key: string]: number | Record<number, number>;
}

function computeAssessmentStats(assessments: Assessment[]): AssessmentStats {
  const stats: AssessmentStats = {
    total: 0, affected: 0, minor: 0, major: 0, destroyed: 0, inaccessible: 0,
    bySeverity: {},
  };
  const catKeys = ['affected', 'minor', 'major', 'destroyed', 'inaccessible'];
  for (const a of assessments) {
    if (a.assessmentType === 'individual') {
      stats.total++;
      const cat = (a.damageCategory || '').toLowerCase();
      if (catKeys.includes(cat)) (stats[cat] as number)++;
      const sev = a.severity || 0;
      stats.bySeverity[sev] = (stats.bySeverity[sev] || 0) + 1;
    }
  }
  return stats;
}

function pct(n: number, total: number) {
  if (!total) return '0%';
  return `${Math.round((n / total) * 100)}%`;
}

// ── FEMA 009-0-91D — Photo Documentation Sheet ──
// Two photos per page (landscape) with caption, location, date, photographer.
// Header on each page: Disaster ID, Applicant, Date, Sheet number.
export interface PhotoDocEntry {
  base64: string;            // image dataURL
  caption: string;
  address?: string;
  county?: string;
  damageCategory?: string;
  severity?: string;
  latitude?: string;
  longitude?: string;
  exifDate?: string;
  assessorName?: string;
  assessmentDate?: string;
}

export async function generatePhotoDocReport(
  incident: Record<string, unknown>,
  photos: PhotoDocEntry[],
  orgName: string,
) {
  const doc = new jsPDF('portrait');
  const pageW = 210;
  const pageH = 297;
  const margin = 12;
  const headerBottom = 20;
  const footerTop = 288;
  const now = format(new Date(), 'MM/dd/yyyy HH:mm');
  const totalPages = Math.max(1, Math.ceil(photos.length / 2));

  // Layout per slot (2 slots per page)
  const slotH = (footerTop - headerBottom) / 2;          // ~134 each
  const labelH = 6;                                       // "Photo N" line
  const photoH = 78;                                      // image area
  const photoBoxW = pageW - margin * 2;                   // 186
  const metaGap = 3;
  // Slot total used ≈ labelH + 2 + photoH + metaGap + ~38 = ~127 < 134 ✓

  const drawHeader = (pageNum: number) => {
    doc.setFillColor(17, 45, 78);
    doc.rect(0, 0, pageW, 18, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont(undefined as unknown as string, 'bold');
    doc.text('FEMA 009-0-91D — PHOTO DOCUMENTATION SHEET', pageW / 2, 8, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont(undefined as unknown as string, 'normal');
    doc.text(`Disaster: ${String(incident.name || '')}   |   ID: ${String(incident.id || '').slice(0, 8)}   |   ${now}   |   Sheet ${pageNum} of ${totalPages}`, pageW / 2, 14, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  };

  const drawFooter = () => {
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated by ${orgName} — OpenGov Impact`, pageW / 2, pageH - 5, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  };

  if (photos.length === 0) {
    drawHeader(1);
    doc.setFontSize(11);
    doc.text('No photos available for the selected assessments.', pageW / 2, 60, { align: 'center' });
    drawFooter();
    return doc;
  }

  for (let i = 0; i < photos.length; i++) {
    const slot = i % 2;
    if (slot === 0) {
      if (i > 0) doc.addPage();
      drawHeader(Math.floor(i / 2) + 1);
      drawFooter();
    }

    const p = photos[i];
    const slotTop = headerBottom + slot * slotH + 4;     // 4pt top padding
    const labelY = slotTop + labelH - 1;                  // text baseline
    const photoY = slotTop + labelH + 2;                  // top of image box
    const metaY = photoY + photoH + metaGap;

    // "Photo N" label
    doc.setFontSize(10);
    doc.setFont(undefined as unknown as string, 'bold');
    doc.text(`Photo ${i + 1}`, margin, labelY);

    // Photo — fit into box, centered
    try {
      const imgProps = doc.getImageProperties(p.base64);
      const ratio = imgProps.width / imgProps.height;
      let drawW = photoBoxW;
      let drawH = drawW / ratio;
      if (drawH > photoH) {
        drawH = photoH;
        drawW = drawH * ratio;
      }
      const xCentered = margin + (photoBoxW - drawW) / 2;
      const yCentered = photoY + (photoH - drawH) / 2;
      // Subtle border around the photo area
      doc.setDrawColor(220, 220, 220);
      doc.rect(margin, photoY, photoBoxW, photoH);
      doc.addImage(p.base64, 'JPEG', xCentered, yCentered, drawW, drawH);
    } catch {
      doc.setFontSize(9);
      doc.setFont(undefined as unknown as string, 'normal');
      doc.setDrawColor(220, 220, 220);
      doc.rect(margin, photoY, photoBoxW, photoH);
      doc.text('[image could not be rendered]', margin + 4, photoY + 12);
    }

    // Compact metadata table beneath the photo
    autoTable(doc, {
      startY: metaY,
      body: [
        ['Caption', p.caption || '—'],
        ['Location', [p.address, p.county].filter(Boolean).join(', ') || '—'],
        ['Coordinates', p.latitude && p.longitude ? `${p.latitude}, ${p.longitude}` : '—'],
        ['Category / Severity', `${(p.damageCategory || '—').toUpperCase()}  |  Severity ${p.severity || '—'}`],
        ['Date / Photographer', `${p.exifDate || p.assessmentDate || '—'}  |  ${p.assessorName || '—'}`],
      ],
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.2, overflow: 'ellipsize', lineColor: [200, 200, 200] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 }, 1: { cellWidth: pageW - margin * 2 - 45 } },
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2,
    });
  }

  return doc;
}

// ── CSV Export ──
export function assessmentsToCSV(assessments: Assessment[]) {
  const headers = [
    'Type', 'Damage Category', 'PA Category', 'Severity', 'Address', 'County', 'FIPS Code',
    'State', 'Lat', 'Lng', 'Residence Type', 'Ownership', 'Dwelling Type', 'Habitable',
    'Insured', 'Water Line Height', 'Occupants', 'Facility Name',
    'Damage Types', 'Description', 'Assessor', 'Date',
  ];

  const rows = assessments.map(a => [
    a.assessmentType || '',
    a.damageCategory || '',
    a.paCategory || '',
    a.severity || '',
    a.address || '',
    a.county || '',
    a.fipsCode || '',
    a.stateCode || '',
    a.latitude || '',
    a.longitude || '',
    a.residenceType || '',
    a.ownership || '',
    a.dwellingType || '',
    a.habitable != null ? (a.habitable ? 'Yes' : 'No') : '',
    a.insured != null ? (a.insured ? 'Yes' : 'No') : '',
    a.waterLineHeight || '',
    a.occupants || '',
    a.facilityName || '',
    (a.damageTypes || []).join('; '),
    (a.description || '').replace(/"/g, '""'),
    a.assessorName || '',
    a.datecreated || '',
  ]);

  const escape = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`;
  return [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
}
